'use client';

import { useEffect, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { useI18n } from 'next-localization';

import { isEmpty, isString } from 'lodash-es';

import { SafeHtml } from '@/components/safe-html';
import { useBasePath } from '@/hooks/use-base-path';
import { useCustomRouting } from '@/hooks/use-custom-routing';
import { useSessionStorageWithCollection } from '@/hooks/use-session-storage-collection';
import { BOOKING_CONFIRMATION_DATA } from '@/lib/constants';
import { NeuImage } from '@hcahealthcaredpx/neutron-react';

import { CreateCalendarButton, PrintButton, ViewOnMapButton } from './components/confirmation';
import { formatAppointment } from './components/step-three';
import styles from './booking.module.css';
import { BookingProps, ConfirmationData } from './booking.types';
import { useBookingContext } from './booking-context';

const normalizeStyles = (html: string) => {
  if (!isString(html)) return '';

  let sanitizedHtml = html;

  // remove empty paragraphs and ones that include &nbsp; or only spaces
  sanitizedHtml = sanitizedHtml.replace(/<p[^>]*>(\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, '');

  // remove margin, padding, line-height, fontfamily, font-size, color, text-indent, from all tags
  // Preserve font-family for spans with font-family: Symbol
  sanitizedHtml = sanitizedHtml.replace(
    /(<span[^>]*style="[^"]*)font-family\s*:\s*Symbol\s*;?([^"]*"[^>]*>)/gi,
    (_, p1, p2) => `${p1}font-family:Symbol;${p2}`
  );

  // Preserve font-family: Symbol on spans by replacing it with a placeholder
  sanitizedHtml = sanitizedHtml.replace(
    /(<span[^>]*style="[^"]*)font-family\s*:\s*Symbol\s*;?/gi,
    (_, p1) => `${p1}__PRESERVE_SYMBOL__`
  );

  // Remove unwanted styles from all tags
  sanitizedHtml = sanitizedHtml.replace(
    /\b(margin|padding|line-height|font-family|font-size|color|text-indent)\s*:\s*([^;"]+);?/gi,
    ''
  );

  // Remove javascript: and data: from style attributes (e.g., background:url(javascript:...))
  sanitizedHtml = sanitizedHtml.replace(/style\s*=\s*"(.*?)"/gi, (_match, styleContent) => {
    // Remove any occurrence of javascript: or data: in style values
    const safeStyle = styleContent.replace(/(url\s*\(\s*['"]?\s*)(javascript:|data:)/gi, '$1');
    // Remove expression() (old IE hack)
    const noExpression = safeStyle.replace(/expression\s*\([^)]*\)/gi, '');
    return `style="${noExpression}"`;
  });

  // Restore the preserved font-family: Symbol on spans
  sanitizedHtml = sanitizedHtml.replace(/__PRESERVE_SYMBOL__/g, 'font-family:Symbol;');

  // Ensure all links open in a new tab by adding target="_blank" if not already present
  sanitizedHtml = sanitizedHtml.replace(/<a(?![^>]*\btarget=)[^>]*>/gi, (match) => {
    // If anchor already has target attribute, leave it unchanged
    if (/target\s*=\s*['"]?_blank['"]?/i.test(match)) return match;
    // Otherwise, add target="_blank"
    return match.replace('<a', '<a rel="noopener noreferrer" target="_blank"');
  });

  return sanitizedHtml;
};

const CONFIRMATION_URL = 'confirmation/';
const CONFIRMATION_REDIRECT_TIMEOUT_MS = 60000; // 60 seconds

export const Confirmation = ({ rendering }: BookingProps): JSX.Element | null => {
  const { t } = useI18n();

  const { confirmationData } = useBookingContext();
  const { getFromStorage } = useSessionStorageWithCollection();
  const { routerPush } = useCustomRouting();
  const pathname = usePathname();
  const { withBasePath } = useBasePath();

  const hasLocalStorage = typeof window !== 'undefined';

  const holdId = pathname.split(CONFIRMATION_URL)[1];
  const confirmationDataFromStorage = hasLocalStorage
    ? getFromStorage({
        key: BOOKING_CONFIRMATION_DATA,
        itemKey: holdId || '',
      })
    : null;

  const confirmationInfo: ConfirmationData | null = useMemo(() => {
    const parsedConfirmationDataFromStorage = confirmationDataFromStorage
      ? confirmationDataFromStorage
      : null;
    return confirmationData ? confirmationData : parsedConfirmationDataFromStorage;
  }, [confirmationDataFromStorage, confirmationData]);

  // Add use effect with timeout to check if confirmationData is null,
  // if so redirect to home page or booking page
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!confirmationInfo || !window) {
        const url = withBasePath('/');

        routerPush(url);
      }
    }, CONFIRMATION_REDIRECT_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [confirmationInfo, routerPush, withBasePath]);

  if (!confirmationInfo || !window) {
    return (
      <section className="container py-40 px-40 mt-5 mb-5" data-test-id={rendering?.componentName}>
        <h3 className="fw-bold mb-2">
          {t('OAS.Booking.Confirmation.NoConfirmationData') ||
            'No confirmation data available.'}{' '}
        </h3>
        <p>
          {t('OAS.Booking.Confirmation.CheckEmail') ||
            'Please check your email for confirmation details.'}
        </p>
      </section>
    );
  }

  const {
    dateTimeOfAppointment,
    location,
    physician,
    appointmentTypeLabel,
    reasonForVisit,
    _response,
  } = confirmationInfo;
  const informationForConsumer = normalizeStyles(_response.informationForConsumer);

  const locationData = {
    name: location?.name || '',
    street: location?.street || '',
    city: location?.city || '',
    state: location?.state || '',
    zip: location?.zip || '',
    location: location?.phone || '',
  };

  return (
    <div data-test-id={rendering?.componentName} className={styles.confirmationWrapper}>
      <header className="py-40">
        <span className="sr-only" role="status">
          {t('OAS.Booking.Confirmation.SuccessfullyBooked')}
        </span>

        <div className="container">
          <h4 className="fw-bold mb-1">{t('OAS.Booking.Confirmation.Title')}</h4>
          <span className="fw-light">{t('OAS.Booking.Confirmation.SubTitle')}</span>
        </div>
      </header>

      <div className={styles.confirmationContent}>
        <div className="container">
          <div className="row">
            <div className="col-sm-12 col-md-8 py-40">
              <div>
                <span className="neu-display-5 mb-2 mb-md-1">
                  {t('OAS.Booking.Confirmation.ConfirmAppointmentFor')}
                </span>
                <h5 className="fw-bold">{formatAppointment(dateTimeOfAppointment)}</h5>
                <br />
                {location?.phone && (
                  <span className="neu-display-6">
                    {t('OAS.Booking.Confirmation.ForQuestionsCall')}{' '}
                    <a href={`tel:${location?.phone}`} aria-label={`Call ${location?.phone}`}>
                      {location?.phone}
                    </a>
                  </span>
                )}
              </div>
            </div>
            <div className="col-sm-12 col-md-4 py-40">
              <PrintButton />
              <CreateCalendarButton appointment={confirmationInfo} />
            </div>
          </div>
          <div className="row border-top py-50">
            <div className="col-12">
              <h5 className="fw-bold">{t('OAS.Booking.Confirmation.AppointmentDetails')}</h5>
              <br />
            </div>
            <div className="col-sm-12 col-md-4">
              {physician && (
                <div className="d-flex flex-wrap">
                  <div className="me-4 mb-2">
                    <NeuImage
                      src={physician.physicianPhotoUrl}
                      height={140}
                      width={140}
                      alt={`${physician.physicianFirstName} ${physician.physicianLastName} image`}
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mb-4">
                    <span className="neu-display-6 fw-bold mb-4 d-block">
                      {physician.physicianFirstName} {physician.physicianMiddleInitial}{' '}
                      {physician.physicianLastName}, {physician.physicianDesignation}
                    </span>
                    <span className="fw-semibold">{physician.providerSpecialty}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="col-sm-12 col-md-4 mb-4">
              <span className="neu-display-6 fw-bold d-block mb-4">{location?.name}</span>
              <span className="neu-display-6 d-block">
                {location?.street}
                <br />
                {location?.city}, {location?.state} {location?.zip}
              </span>
              <a href={`tel:${location?.phone}`} aria-label={`Call ${location?.phone}`}>
                {location?.phone}
              </a>
            </div>
            <div className="col-sm-12 col-md-4 mb-4">
              <ViewOnMapButton location={locationData} />
            </div>
            <div className="col-sm-12 col-md-4 mb-2 mb-md-0">
              <span className="neu-display-6 fw-bold">
                {t('OAS.Booking.Confirmation.AppointmentType')}
              </span>
              <p>{appointmentTypeLabel}</p>
            </div>
            <div className="col-sm-12 col-md-4">
              <span className="neu-display-6 fw-bold mb-4 d-block">
                {t('OAS.Booking.Confirmation.ReasonForVisit')}
              </span>
              <p>{reasonForVisit}</p>
            </div>
          </div>
          <div className="row border-top py-40">
            <div className="col-12 mb-4">
              <h5 className="fw-bold">{t('OAS.Booking.Confirmation.ThingsToKnow')}</h5>
            </div>
            <div className="col-12">
              {/* Instead of just dangerouslySetInnerHTML, parse and sanitize the HTML before rendering */}
              {isString(informationForConsumer) && !isEmpty(informationForConsumer) && (
                <SafeHtml html={informationForConsumer} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
